import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { plantaApi } from "../../api/fetch";
import { BadRequest } from "../../api/types.mjs";
import { orgNodes$ } from "./state";
import type { ClientBusinessNode, GetOrgUnitsResponse } from "./types";

interface OrgStructureResponse {
  readonly nodes: ReadonlyArray<ClientBusinessNode>;
}

interface UseOrgQueryParams {
  readonly signal?: AbortSignal;
  readonly enabled?: boolean;
}

interface UseGetOrgStructureParams extends UseOrgQueryParams {
  readonly countryCode?: string;
}

export const useGetOrgStructure = ({
  signal,
  enabled,
  countryCode,
}: UseGetOrgStructureParams): {
  data: OrgStructureResponse | null;
  refetch: () => void;
  isLoading: boolean;
} => {
  const queryParams =
    countryCode !== "" && countryCode !== undefined
      ? `?countryCode=${countryCode}`
      : "";

  const { data, refetch, isLoading } = useQuery<OrgStructureResponse | null>({
    initialData: null,
    queryKey: [`org-structure`, countryCode],
    queryFn: async () => {
      const res = await plantaApi.jsonInJsonOut<OrgStructureResponse>(
        `/headcount/organisation-structure${queryParams}`,
        { signal },
      );

      if (res.nodes != null) {
        orgNodes$.value = res.nodes;
      }
      return res;
    },
    enabled,
  });

  useEffect(() => {
    if (data?.nodes != null && orgNodes$.value.length === 0) {
      orgNodes$.value = data.nodes;
    }
  }, [data]);

  return {
    data,
    refetch,
    isLoading,
  };
};

export const useGetOrgUnits = ({
  enabled,
  signal,
}: UseOrgQueryParams): {
  data: GetOrgUnitsResponse[];
  refetch: () => void;
  isLoading: boolean;
} => {
  const { data, refetch, isLoading } = useQuery<GetOrgUnitsResponse[] | null>({
    initialData: null,
    queryKey: [`org-units`],
    enabled,
    queryFn: async () => {
      const res = await plantaApi.jsonInJsonOut<GetOrgUnitsResponse[]>(
        `/headcount/organisation-units`,
        { signal },
      );

      return res;
    },
  });

  return {
    data: data ?? [],
    refetch,
    isLoading,
  };
};

interface CreatedActivity {
  data: string;
}

type CrateActivityResponse = CreatedActivity | BadRequest;

export const putActivity = async (
  department: string,
  name: string,
  description: string,
): Promise<CrateActivityResponse> => {
  try {
    return await plantaApi.jsonInJsonOut<CrateActivityResponse>(`/activity`, {
      method: "PUT",
      body: JSON.stringify({
        activity: name,
        departmentId: department,
        description,
      }),
    });
  } catch (error) {
    // @ts-expect-error error is a Response with known schema
    return { error: (await error.response.json()).error };
  }
};

export const refreshServerOrgCache = async (
  countryCode?: string,
): Promise<void> => {
  const queryParams =
    countryCode !== "" && countryCode !== undefined
      ? `?countryCode=${countryCode}`
      : "";

  await plantaApi.jsonInVoidOut(
    `/headcount/organisation-structure/refresh${queryParams}`,
    {
      method: "GET",
    },
  );
};
